// =====================================================================
// ItemFormModal.jsx - Create/edit InventoryItem (M-004 B2.1b)
// =====================================================================
// Pattern simplu: project dropdown + name + unit + notes + active toggle.
// Unit dropdown cu 6 optiuni standard + "Autre..." pentru custom input.
// =====================================================================

import { useState, useEffect } from 'react';
import {
  inventoryItems as itemsApi,
  projects as projectsApi,
} from '../../services/api.js';
import { useAuth } from '../../hooks/useAuth.jsx';

const UNIT_OPTIONS = [
  'Palette(s)',
  'Carton(s)',
  'Unité(s)',
  'Étagère(s)',
  'Vélo(s)',
  'm²',
];

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
};

const modalStyle = {
  background: '#fff',
  borderRadius: 8,
  padding: '1.5rem 1.75rem',
  width: '90%',
  maxWidth: 520,
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: '#374151',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '0.375rem',
};

const inputStyle = {
  width: '100%',
  padding: '0.5rem 0.625rem',
  border: '1px solid #d1d5db',
  borderRadius: 4,
  fontSize: '0.875rem',
  background: '#fff',
  boxSizing: 'border-box',
};

const btnPrimary = {
  background: '#2563eb', color: '#fff', border: 0,
  padding: '0.5rem 1.25rem', borderRadius: 4,
  fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer',
};

const btnSecondary = {
  background: 'transparent', color: '#6b7280',
  border: '1px solid #d1d5db', padding: '0.5rem 1.25rem',
  borderRadius: 4, fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer',
};

const btnDanger = {
  background: 'transparent', color: '#b91c1c',
  border: '1px solid #fca5a5', padding: '0.5rem 1.25rem',
  borderRadius: 4, fontWeight: 500, fontSize: '0.875rem', cursor: 'pointer',
};

export default function ItemFormModal({ itemId, onClose, onSaved }) {
  const { can } = useAuth();
  const isEdit = !!itemId;

  const canCreate = can('inventory.create.all');
  const canUpdate = can('inventory.update.all');
  const canDelete = can('inventory.delete.all');

  // Form state
  const [projectId, setProjectId]   = useState('');
  const [name, setName]             = useState('');
  const [unit, setUnit]             = useState('Palette(s)');
  const [unitCustom, setUnitCustom] = useState(''); // pentru "Autre..."
  const [isUnitCustom, setIsCustom] = useState(false);
  const [notes, setNotes]           = useState('');
  const [isActive, setIsActive]     = useState(true);
  const [ref, setRef]               = useState('');

  // Dropdown data
  const [projects, setProjects] = useState([]);

  // UI state
  const [loading, setLoading]   = useState(isEdit);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const projData = await projectsApi.list({ isActive: true });
        if (cancelled) return;
        setProjects(Array.isArray(projData) ? projData.sort((a, b) => a.name.localeCompare(b.name)) : []);

        if (isEdit) {
          const it = await itemsApi.get(itemId);
          if (cancelled) return;
          setRef(it.ref);
          setProjectId(it.projectId);
          setName(it.name);
          if (UNIT_OPTIONS.includes(it.unit)) {
            setUnit(it.unit);
            setIsCustom(false);
          } else {
            setUnit('__custom__');
            setUnitCustom(it.unit);
            setIsCustom(true);
          }
          setNotes(it.notes || '');
          setIsActive(it.isActive);
        }
      } catch (err) {
        console.error('[ItemFormModal] load:', err);
        setError(err.response?.data?.message || 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [itemId, isEdit]);

  // Escape closes
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleUnitChange(value) {
    if (value === '__custom__') {
      setIsCustom(true);
      setUnit('__custom__');
    } else {
      setIsCustom(false);
      setUnit(value);
      setUnitCustom('');
    }
  }

  async function handleSubmit(e) {
    e?.preventDefault();
    setError(null);

    if (!projectId) return setError('Projet requis');
    if (!name.trim()) return setError('Nom requis');
    const finalUnit = isUnitCustom ? unitCustom.trim() : unit;
    if (!finalUnit) return setError('Unité requise');

    setSaving(true);
    try {
      const data = {
        projectId,
        name: name.trim(),
        unit: finalUnit,
        notes: notes.trim() || null,
        isActive,
      };
      if (isEdit) {
        await itemsApi.update(itemId, data);
      } else {
        await itemsApi.create(data);
      }
      onSaved();
    } catch (err) {
      console.error('[ItemFormModal] save:', err);
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Supprimer l'item ${ref} ?\n\nNote: si l'item a des comptages associés, il sera désactivé au lieu d'être supprimé.`)) return;
    setSaving(true);
    try {
      await itemsApi.remove(itemId);
      onSaved();
    } catch (err) {
      console.error('[ItemFormModal] delete:', err);
      setError(err.response?.data?.message || 'Erreur lors de la suppression');
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div style={overlayStyle} onClick={onClose}>
        <div className="modal-mobile-fullscreen" style={modalStyle} onClick={e => e.stopPropagation()}>
          <p style={{ textAlign: 'center', color: '#6b7280' }}>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div className="modal-mobile-fullscreen" style={modalStyle} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '1.25rem' }}>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
            {isEdit ? 'Modifier item' : 'Nouvel item'}
          </h2>
          {isEdit && (
            <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontFamily: 'monospace' }}>{ref}</span>
          )}
        </div>

        {error && (
          <div style={{ background: '#fee2e2', color: '#991b1b', padding: '0.625rem 0.875rem', borderRadius: 4, marginBottom: '1rem', fontSize: '0.8125rem' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Project */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Projet *</label>
            <select
              value={projectId}
              onChange={e => setProjectId(e.target.value)}
              style={inputStyle}
              disabled={saving}
              required
            >
              <option value="">— Sélectionner un projet —</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            {isEdit && (
              <small style={{ color: '#9ca3af', fontSize: '0.6875rem' }}>
                ⚠️ Changer le projet réaffecte l'item (et sa facturation future) au nouveau projet.
              </small>
            )}
          </div>

          {/* Name */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Nom de l'item *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              style={inputStyle}
              maxLength={200}
              disabled={saving}
              placeholder="Ex: Palettes EURO, Étagères Linotrenor..."
              required
            />
          </div>

          {/* Unit */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Unité *</label>
            <select
              value={unit}
              onChange={e => handleUnitChange(e.target.value)}
              style={inputStyle}
              disabled={saving}
              required
            >
              {UNIT_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
              <option value="__custom__">Autre...</option>
            </select>
            {isUnitCustom && (
              <input
                type="text"
                value={unitCustom}
                onChange={e => setUnitCustom(e.target.value)}
                style={{ ...inputStyle, marginTop: 8 }}
                placeholder="Saisir l'unité personnalisée"
                maxLength={50}
                disabled={saving}
                required
                autoFocus
              />
            )}
          </div>

          {/* Notes */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={labelStyle}>Notes (optionnel)</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              style={{ ...inputStyle, minHeight: 60, fontFamily: 'inherit' }}
              maxLength={500}
              disabled={saving}
              placeholder="Description, contexte..."
            />
          </div>

          {/* Active toggle */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem', color: '#374151', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={isActive}
                onChange={e => setIsActive(e.target.checked)}
                disabled={saving}
              />
              Item actif (apparait dans la liste de saisie des comptages)
            </label>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              {isEdit && canDelete && (
                <button type="button" style={btnDanger} onClick={handleDelete} disabled={saving}>
                  Supprimer
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button type="button" style={btnSecondary} onClick={onClose} disabled={saving}>
                Annuler
              </button>
              {(canCreate || (isEdit && canUpdate)) && (
                <button type="submit" style={btnPrimary} disabled={saving}>
                  {saving ? 'Enregistrement...' : (isEdit ? 'Enregistrer' : 'Créer')}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
